// src/service/api/employeeApi.js

import axiosInstance from "utils/axiosInstance";

const employeeApi = {
  /**
   * Fetches a list of all employees.
   * @returns {Promise<Array>} A promise that resolves to an array of employee objects.
   */
  getEmployees: async () => {
    const response = await axiosInstance.get("/employee/getall");
    return response?.data?.data?.employees || [];
  },

  /**
   * Fetches a list of all departments.
   * @returns {Promise<Array>} A promise that resolves to an array of department objects.
   */
  getDepartments: async () => {
    const response = await axiosInstance.get("/departments/getall");
    return response.data || [];
  },

  /**
   * Creates a new employee.
   * @param {FormData} employeeData - The form data for the new employee.
   * @returns {Promise<Object>} A promise that resolves to the created employee object.
   */
  createEmployee: async (employeeData) => {
    const response = await axiosInstance.post("/admin/register-employee", employeeData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data?.data?.employee;
  },

  deleteEmployeeImage: async (imageId) => {
    const response = await axiosInstance.delete(`/employee/delete-image/${imageId}`);
    return response.data;
  },

  deleteEmployeeImagesBulk: async (employeeIds) => {
    const response = await axiosInstance.delete("/employee/delete-images-bulk", {
      data: { employee_ids: employeeIds },
    });
    return response.data;
  },
  /**
   * Updates an existing employee's details.
   * @param {string} employeeId - The ID of the employee to update.
   * @param {FormData} employeeData - The form data with updated employee information.
   * @returns {Promise<Object>} A promise that resolves to the updated employee object.
   */
  updateEmployee: async (employeeId, employeeData) => {
    const response = await axiosInstance.put(
      `/admin/update-profile/${employeeId}`,
      employeeData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response?.data?.data?.employee;
  },

  /**
   * Activates one or more employees.
   * @param {Array<string>} employeeIds - An array of employee IDs to activate.
   */
  activateEmployees: async (employeeIds) => {
    await axiosInstance.put("/employee/update/bulk/activate", {
      employee_ids: employeeIds,
    });
  },

  /**
   * Deactivates one or more employees.
   * @param {Array<string>} employeeIds - An array of employee IDs to deactivate.
   */
  deactivateEmployees: async (employeeIds) => {
    await axiosInstance.put("/employee/update/bulk/deactivate", {
      employee_ids: employeeIds,
    });
  },

  /**
   * Suspends one or more employees.
   * @param {Array<string>} employeeIds - An array of employee IDs to suspend.
   */
  suspendEmployees: async (employeeIds) => {
    await axiosInstance.put("/employee/update/bulk/suspend", {
      employee_ids: employeeIds,
    });
  },

  /**
   * Deletes one or more employees.
   * @param {Array<string>} employeeIds - An array of employee IDs to delete.
   */
  deleteEmployees: async (employeeIds) => {
    await axiosInstance.delete("/employee/stranger/bulk/delete", {
      data: { employee_ids: employeeIds },
    });
  },

  /**
   * Exports all employees to a CSV file.
   * @returns {Promise<Blob>} A promise that resolves to the CSV data as a Blob.
   */
  exportAllEmployees: async () => {
    const response = await axiosInstance.get("employee/get/export/csv", {
      responseType: "blob",
    });
    return response.data;
  },

  /**
   * Exports a selection of employees to a CSV file.
   * @param {Array<string>} employeeIds - An array of employee IDs to export.
   * @returns {Promise<Blob>} A promise that resolves to the CSV data as a Blob.
   */
  exportSelectedEmployees: async (employeeIds) => {
    const response = await axiosInstance.post("/employee/bulk/export", {
      employee_ids: employeeIds,
    }, {
      responseType: "blob",
    });
    return response.data;
  },

  /**
   * Toggles the status of a single employee (activate/deactivate).
   * @param {Object} employee - The employee object to toggle status for.
   * @returns {Promise<void>}
   */
  toggleEmployeeStatus: async (employee) => {
    if (employee?.status !== 'active') {
      await axiosInstance.put("/employee/update/bulk/activate", {
        employee_ids: [String(employee?.employee_id)]
      });
    } else {
      await axiosInstance.put("/employee/update/bulk/deactivate", {
        employee_ids: [String(employee?.employee_id)]
      });
    }
  },
};

export default employeeApi;